import { create } from "zustand"
import { UserProfile } from "../models/userProfile.model"
import { CruiseData } from "../models/cruiseData.model"
import { AgencyAffiliation } from "../models/agencyAffiliation.model"
import { StorageKeys } from "@utils/user-config"
import secureLocalStorage from "react-secure-storage"
import { getRuntimeEnv } from "@utils/env"
import {
	MULTI_AGENCY_ALLOWED_OFFICES,
	MULTI_AGENCY_ALL_OFFICES_ALLOWED
} from "@utils/constants.util"

interface UserStore {
	userProfile: UserProfile | null
	setUserProfile: (profile: UserProfile | null) => void
	extendUserProfile: (data: CruiseData) => void
	activeAgencyId: number | null
	availableAffiliations: AgencyAffiliation[]
	setActiveAgency: (id: number) => Promise<void>
	setDefaultAgency: (id: number) => Promise<void>
	setAffiliations: (affiliations: AgencyAffiliation[]) => void
}

const VITE_COUNTRIES_WITH_FLEXPAY =
	getRuntimeEnv("VITE_COUNTRIES_WITH_FLEXPAY")?.split(",") || []

export const useUserStore = create<UserStore>((set, get) => ({
	userProfile: null,
	activeAgencyId:
		Number(secureLocalStorage.getItem(StorageKeys.ACTIVE_AGENCY_ID)) || null,
	availableAffiliations: [],
	setUserProfile: (profile) => {
		if (!profile) {
			secureLocalStorage.removeItem(StorageKeys.ACTIVE_AGENCY_ID)
			set({
				userProfile: null,
				availableAffiliations: [],
				activeAgencyId: null
			})
			return
		}
		const affiliations =
			profile.availableAgencies ?? get().availableAffiliations
		const storedAgencyId = secureLocalStorage.getItem(
			StorageKeys.ACTIVE_AGENCY_ID
		)
		const activeAffiliation =
			(storedAgencyId
				? affiliations.find((a) => a.agencyId === Number(storedAgencyId))
				: null) ??
			affiliations.find((a) => a.isDefault) ??
			affiliations.find((a) => a.active)
		if (activeAffiliation) {
			secureLocalStorage.setItem(
				StorageKeys.ACTIVE_AGENCY_ID,
				String(activeAffiliation.agencyId)
			)
		}
		const normalizedAffiliations = activeAffiliation
			? affiliations.map((a) => ({
					...a,
					active: a.agencyId === activeAffiliation.agencyId
				}))
			: affiliations
		set({
			userProfile: {
				...profile,
				hasFlexPayAccess: VITE_COUNTRIES_WITH_FLEXPAY?.includes(
					profile.countryCode || ""
				),
				hasMultiAgencyAccess:
					MULTI_AGENCY_ALL_OFFICES_ALLOWED ||
					MULTI_AGENCY_ALLOWED_OFFICES.includes(
						profile.reportingOfficeCode || ""
					),
				primaryCurrency:
					profile.currencies.find((currency) => currency.primaryCurrency)
						?.currencyCode || "USD"
			},
			availableAffiliations: normalizedAffiliations,
			activeAgencyId: activeAffiliation?.agencyId ?? null
		})
	},
	extendUserProfile: (data) => {
		const currentProfile = get().userProfile
		if (currentProfile) {
			set({
				userProfile: {
					...currentProfile,
					...data
				}
			})
		}
	},
	setAffiliations: (affiliations) => {
		set({ availableAffiliations: affiliations })
	},
	setDefaultAgency: async (id) => {
		// Optimistic local update — API call handled by useSetDefaultAgency hook
		set({
			availableAffiliations: get().availableAffiliations.map((a) => ({
				...a,
				isDefault: a.agencyId === id
			}))
		})
	},
	setActiveAgency: async (id) => {
		// Local state only — storage is owned by useSetActiveAgency mutation.
		// Do NOT write storage here: a stale closure can clobber a fresh switch.
		set({ activeAgencyId: id })
	}
}))
